<template>
  <div class="page-wrapper">
    <HeaderComponent />

    <main class="main-content">
      <!-- Page Hero -->
      <section class="page-hero">
        <div class="container">
          <div class="hero-content">
            <nav class="breadcrumb-nav">
              <a href="/" class="breadcrumb-link">Home</a>
              <span class="breadcrumb-separator">/</span>
              <span class="breadcrumb-current">Building Permits</span>
            </nav>
            <h1 class="page-title">Building Permits</h1>
            <p class="page-subtitle">Apply for construction and development permits</p>
          </div>

          <!-- Progress Steps -->
          <div class="progress-stepper">
            <div
                v-for="(step, index) in steps"
                :key="index"
                class="step-item"
                :class="{
                active: currentStep === index + 1,
                completed: currentStep > index + 1
              }"
            >
              <div class="step-connector" v-if="index > 0"></div>
              <div class="step-circle">
                <svg v-if="currentStep > index + 1" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span v-else>{{ index + 1 }}</span>
              </div>
              <span class="step-label">{{ step }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Main Content -->
      <section class="content-section">
        <div class="container">
          <div class="content-grid">
            <!-- Left Side - Info Panel -->
            <div class="info-panel">
              <div class="info-card">
                <div class="info-card-header">
                  <div class="info-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                      <path d="M2 20h20M5 20V8l7-5 7 5v12M9 20v-6h6v6M9 12h.01M15 12h.01M9 8h.01M15 8h.01"></path>
                    </svg>
                  </div>
                  <h3>Construction Permits</h3>
                </div>
                <p class="info-description">
                  Apply for building and construction permits online. All applications are reviewed by the County Physical Planning department.
                </p>

                <div class="info-features">
                  <div class="feature-item">
                    <div class="feature-icon green">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                      </svg>
                    </div>
                    <div>
                      <strong>Online Application</strong>
                      <p>Submit applications from anywhere</p>
                    </div>
                  </div>

                  <div class="feature-item">
                    <div class="feature-icon blue">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                    </div>
                    <div>
                      <strong>Track Progress</strong>
                      <p>Monitor your application status</p>
                    </div>
                  </div>

                  <div class="feature-item">
                    <div class="feature-icon purple">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                      </svg>
                    </div>
                    <div>
                      <strong>Secure Process</strong>
                      <p>All data is encrypted and secure</p>
                    </div>
                  </div>
                </div>

                <!-- Application Process -->
                <div class="process-info">
                  <h4>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="16" x2="12" y2="12"></line>
                      <line x1="12" y1="8" x2="12.01" y2="8"></line>
                    </svg>
                    Application Process
                  </h4>
                  <ol>
                    <li>Submit your application online</li>
                    <li>Application is reviewed by Physical Planning</li>
                    <li>Receive invoice via SMS &amp; Email</li>
                    <li>Make payment for the permit</li>
                    <li>Collect your approved permit</li>
                  </ol>
                </div>

                <!-- Required Documents -->
                <div class="required-docs">
                  <h4>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                    </svg>
                    Required Documents
                  </h4>
                  <ul>
                    <li>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      Architectural Drawings
                    </li>
                    <li>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      Structural Drawings
                    </li>
                    <li>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      Title Deed / Lease Agreement
                    </li>
                    <li>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      Survey Plan / Site Plan
                    </li>
                    <li>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      Owner's ID Card
                    </li>
                    <li>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      KRA PIN Certificate
                    </li>
                  </ul>
                  <p class="docs-note">PDF format • Max 10MB per file</p>
                </div>

                <!-- Help Contact -->
                <div class="help-contact">
                  <h4>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                      <line x1="12" y1="17" x2="12.01" y2="17"></line>
                    </svg>
                    Need Help?
                  </h4>
                  <div class="contact-info">
                    <div class="contact-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                      </svg>
                      <span>{{ county?.contact || '0700-000-000' }}</span>
                    </div>
                    <div class="contact-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                        <polyline points="22,6 12,13 2,6"></polyline>
                      </svg>
                      <span>{{ county?.email || 'planning@county.go.ke' }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right Side - Form Panel -->
            <div class="form-panel">
              <!-- Error Alert -->
              <div v-if="error" class="alert alert-error mb-4">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
                <span>{{ error }}</span>
                <button @click="error = ''" class="alert-close">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>

              <!-- Step 1: Development Type & Project Info -->
              <transition name="slide-fade" mode="out-in">
                <div v-if="currentStep === 1" key="step1" class="form-card">
                  <div class="form-card-header">
                    <div class="step-badge">
                      <span class="step-number">1</span>
                      <span class="step-text">Project Information</span>
                    </div>
                  </div>

                  <div class="form-card-body">
                    <p class="form-intro">
                      Provide details about your construction project.
                    </p>

                    <div class="form-group">
                      <label class="form-label">
                        Development Type <span class="text-danger">*</span>
                      </label>
                      <div class="development-type-grid">
                        <label
                            v-for="(type, index) in developmentTypes"
                            :key="index"
                            class="type-card"
                            :class="{ selected: formData.developmentType === type.value }"
                        >
                          <input
                              type="radio"
                              v-model="formData.developmentType"
                              :value="type.value"
                              class="hidden-radio"
                          />
                          <div class="type-icon" :class="type.color">
                            <component :is="type.icon" />
                          </div>
                          <strong>{{ type.label }}</strong>
                        </label>
                      </div>
                    </div>

                    <div class="form-group">
                      <label class="form-label">
                        Project Title <span class="text-danger">*</span>
                      </label>
                      <input
                          type="text"
                          v-model="formData.projectTitle"
                          class="form-input"
                          placeholder="e.g., Residential House Construction"
                      />
                    </div>

                    <div class="form-row">
                      <div class="form-group">
                        <label class="form-label">Estimated Project Value (KES) <span class="text-danger">*</span></label>
                        <input
                            type="number"
                            v-model="formData.projectValue"
                            class="form-input"
                            placeholder="e.g., 5000000"
                        />
                      </div>
                      <div class="form-group">
                        <label class="form-label">Plot/LR Number <span class="text-danger">*</span></label>
                        <div class="input-with-button">
                          <input
                              type="text"
                              v-model="formData.plotNumber"
                              class="form-input"
                              placeholder="Enter Plot/LR Number"
                              @keyup.enter="searchPlot"
                          />
                          <button
                              type="button"
                              class="fetch-btn"
                              @click="searchPlot"
                              :disabled="!formData.plotNumber || searchingPlot"
                              title="Search for plot details"
                          >
                            <span v-if="searchingPlot" class="spinner small"></span>
                            <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <circle cx="11" cy="11" r="8"></circle>
                              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                            </svg>
                          </button>
                        </div>
                        <span class="form-hint">Enter plot number and click search to fetch details</span>
                      </div>
                    </div>

                    <!-- Plot Details Card -->
                    <div v-if="plotDetails" class="plot-details-card">
                      <div class="plot-details-header">
                        <div class="plot-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                            <circle cx="12" cy="10" r="3"></circle>
                          </svg>
                        </div>
                        <div>
                          <h4>Plot Found</h4>
                          <p>{{ plotDetails.LRNumber }}</p>
                        </div>
                        <button type="button" class="clear-plot-btn" @click="clearPlotDetails" title="Clear">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                          </svg>
                        </button>
                      </div>
                      <div class="plot-details-grid">
                        <div class="plot-detail-item">
                          <span class="detail-label">Owner</span>
                          <span class="detail-value">{{ plotDetails.ownerNames || 'N/A' }}</span>
                        </div>
                        <div class="plot-detail-item">
                          <span class="detail-label">Location</span>
                          <span class="detail-value">{{ plotDetails.wardName }}, {{ plotDetails.subCountyName }}</span>
                        </div>
                        <div class="plot-detail-item" v-if="plotDetails.percelSize">
                          <span class="detail-label">Plot Size</span>
                          <span class="detail-value">{{ plotDetails.percelSize }} m²</span>
                        </div>
                        <div class="plot-detail-item" v-if="plotDetails.UPN">
                          <span class="detail-label">UPN</span>
                          <span class="detail-value">{{ plotDetails.UPN }}</span>
                        </div>
                        <div class="plot-detail-item" v-if="plotDetails.outstandingArrears && parseFloat(plotDetails.outstandingArrears) > 0">
                          <span class="detail-label">Outstanding Arrears</span>
                          <span class="detail-value text-warning">KES {{ formatAmount(plotDetails.outstandingArrears) }}</span>
                        </div>
                        <div class="plot-detail-item">
                          <span class="detail-label">Status</span>
                          <span class="detail-value">
                            <span class="status-badge" :class="plotDetails.status === 'Active' ? 'active' : 'inactive'">
                              {{ plotDetails.status }}
                            </span>
                          </span>
                        </div>
                      </div>
                      <p class="plot-note">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="16" x2="12" y2="12"></line>
                          <line x1="12" y1="8" x2="12.01" y2="8"></line>
                        </svg>
                        Owner details will be pre-filled in the next step
                      </p>
                    </div>

                    <div class="form-group">
                      <label class="form-label">
                        Project Description <span class="text-danger">*</span>
                      </label>
                      <textarea
                          v-model="formData.projectDescription"
                          class="form-textarea"
                          rows="3"
                          placeholder="Brief description of the construction project..."
                      ></textarea>
                    </div>

                    <div class="form-actions">
                      <button
                          class="btn btn-primary btn-lg"
                          @click="nextStep"
                          :disabled="!canProceedStep1"
                      >
                        Continue
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                          <polyline points="12 5 19 12 12 19"></polyline>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Step 2: Plot & Owner Details -->
                <div v-else-if="currentStep === 2" key="step2" class="form-card">
                  <div class="form-card-header">
                    <div class="step-badge">
                      <span class="step-number">2</span>
                      <span class="step-text">Plot & Owner Details</span>
                    </div>
                    <button class="back-btn" @click="prevStep">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="19" y1="12" x2="5" y2="12"></line>
                        <polyline points="12 19 5 12 12 5"></polyline>
                      </svg>
                      Back
                    </button>
                  </div>

                  <div class="form-card-body">
                    <div class="section-header">
                      <h4>Plot Information</h4>
                    </div>

                    <div class="form-row">
                      <div class="form-group">
                        <label class="form-label">UPN Number</label>
                        <input
                            type="text"
                            v-model="formData.upnNumber"
                            class="form-input"
                            placeholder="Unique Parcel Number (if available)"
                        />
                      </div>
                      <div class="form-group">
                        <label class="form-label">Plot Size (m²) <span class="text-danger">*</span></label>
                        <input
                            type="number"
                            v-model="formData.plotSize"
                            class="form-input"
                            placeholder="e.g., 500"
                        />
                      </div>
                    </div>

                    <div class="form-row">
                      <div class="form-group">
                        <label class="form-label">Land Type <span class="text-danger">*</span></label>
                        <div class="select-wrapper">
                          <select v-model="formData.landType" class="form-select">
                            <option value="">-- Select Land Type --</option>
                            <option value="Freehold">Freehold</option>
                            <option value="Leasehold">Leasehold</option>
                            <option value="Community Land">Community Land</option>
                            <option value="Public Land">Public Land</option>
                          </select>
                          <span class="select-icon">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                          </span>
                        </div>
                      </div>
                      <div class="form-group">
                        <label class="form-label">Land Use <span class="text-danger">*</span></label>
                        <div class="select-wrapper">
                          <select v-model="formData.landUse" class="form-select">
                            <option value="">-- Select Land Use --</option>
                            <option value="Residential">Residential</option>
                            <option value="Commercial">Commercial</option>
                            <option value="Industrial">Industrial</option>
                            <option value="Agricultural">Agricultural</option>
                            <option value="Mixed Use">Mixed Use</option>
                            <option value="Institutional">Institutional</option>
                          </select>
                          <span class="select-icon">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                          </span>
                        </div>
                      </div>
                    </div>

                    <div class="form-row">
                      <div class="form-group">
                        <label class="form-label">Sub County <span class="text-danger">*</span></label>
                        <div class="select-wrapper">
                          <select v-model="selectedSubCounty" class="form-select" @change="onSubCountyChange">
                            <option value="">-- Select Sub County --</option>
                            <option v-for="sc in subCounties" :key="sc.subCountyID" :value="sc">
                              {{ sc.subCountyName }}
                            </option>
                          </select>
                          <span class="select-icon">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                          </span>
                        </div>
                      </div>
                      <div class="form-group">
                        <label class="form-label">Ward <span class="text-danger">*</span></label>
                        <div class="select-wrapper">
                          <select v-model="selectedWard" class="form-select" :disabled="!selectedSubCounty">
                            <option value="">-- Select Ward --</option>
                            <option v-for="w in wards" :key="w.wardID" :value="w">
                              {{ w.wardName }}
                            </option>
                          </select>
                          <span class="select-icon">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                          </span>
                        </div>
                      </div>
                    </div>

                    <div class="section-header mt-4">
                      <h4>Property Owner Information</h4>
                    </div>

                    <div class="form-row">
                      <div class="form-group">
                        <label class="form-label">Full Names <span class="text-danger">*</span></label>
                        <input
                            type="text"
                            v-model="formData.ownerFullNames"
                            class="form-input"
                            placeholder="Enter owner's full name"
                        />
                      </div>
                      <div class="form-group">
                        <label class="form-label">ID Number <span class="text-danger">*</span></label>
                        <input
                            type="text"
                            v-model="formData.ownerIdNumber"
                            class="form-input"
                            placeholder="Enter ID number"
                        />
                      </div>
                    </div>

                    <div class="form-row">
                      <div class="form-group">
                        <label class="form-label">Phone Number <span class="text-danger">*</span></label>
                        <input
                            type="tel"
                            v-model="formData.ownerPhoneNumber"
                            class="form-input"
                            placeholder="07XX XXX XXX"
                        />
                      </div>
                      <div class="form-group">
                        <label class="form-label">Email Address</label>
                        <input
                            type="email"
                            v-model="formData.ownerEmail"
                            class="form-input"
                            placeholder="email@example.com"
                        />
                      </div>
                    </div>

                    <div class="form-row">
                      <div class="form-group">
                        <label class="form-label">KRA PIN</label>
                        <input
                            type="text"
                            v-model="formData.ownerKraPin"
                            class="form-input"
                            placeholder="e.g., A123456789B"
                        />
                      </div>
                      <div class="form-group">
                        <label class="form-label">Postal Address</label>
                        <input
                            type="text"
                            v-model="formData.ownerAddress"
                            class="form-input"
                            placeholder="P.O Box XXX - XXXXX"
                        />
                      </div>
                    </div>

                    <div class="form-actions">
                      <button
                          class="btn btn-primary btn-lg"
                          @click="nextStep"
                          :disabled="!canProceedStep2"
                      >
                        Continue
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                          <polyline points="12 5 19 12 12 19"></polyline>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Step 3: Professional Details -->
                <div v-else-if="currentStep === 3" key="step3" class="form-card">
                  <div class="form-card-header">
                    <div class="step-badge">
                      <span class="step-number">3</span>
                      <span class="step-text">Professional Details</span>
                    </div>
                    <button class="back-btn" @click="prevStep">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="19" y1="12" x2="5" y2="12"></line>
                        <polyline points="12 19 5 12 12 5"></polyline>
                      </svg>
                      Back
                    </button>
                  </div>

                  <div class="form-card-body">
                    <p class="form-intro">
                      Provide details of the registered professional (Architect/Engineer) handling this project.
                    </p>

                    <div class="form-group">
                      <label class="form-label">Professional Type <span class="text-danger">*</span></label>
                      <div class="select-wrapper">
                        <select v-model="formData.professionalType" class="form-select">
                          <option value="">-- Select Professional Type --</option>
                          <option value="architect">Architect</option>
                          <option value="civilEngineer">Civil Engineer</option>
                          <option value="structuralEngineer">Structural Engineer</option>
                          <option value="electricalEngineer">Electrical Engineer</option>
                          <option value="mechanicalEngineer">Mechanical Engineer</option>
                          <option value="landSurveyor">Land Surveyor</option>
                          <option value="urbanPlanner">Urban Planner</option>
                        </select>
                        <span class="select-icon">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </span>
                      </div>
                    </div>

                    <div class="form-row">
                      <div class="form-group">
                        <label class="form-label">Full Names <span class="text-danger">*</span></label>
                        <input
                            type="text"
                            v-model="formData.professionalName"
                            class="form-input"
                            placeholder="Enter professional's full name"
                        />
                      </div>
                      <div class="form-group">
                        <label class="form-label">Certificate/Reg. Number <span class="text-danger">*</span></label>
                        <input
                            type="text"
                            v-model="formData.certificateNumber"
                            class="form-input"
                            placeholder="BORAQS/NCA/EBK Reg No."
                        />
                      </div>
                    </div>

                    <div class="form-row">
                      <div class="form-group">
                        <label class="form-label">Phone Number <span class="text-danger">*</span></label>
                        <input
                            type="tel"
                            v-model="formData.professionalPhone"
                            class="form-input"
                            placeholder="07XX XXX XXX"
                        />
                      </div>
                      <div class="form-group">
                        <label class="form-label">Email Address <span class="text-danger">*</span></label>
                        <input
                            type="email"
                            v-model="formData.professionalEmail"
                            class="form-input"
                            placeholder="email@example.com"
                        />
                      </div>
                    </div>

                    <div class="form-row">
                      <div class="form-group">
                        <label class="form-label">ID Number <span class="text-danger">*</span></label>
                        <input
                            type="text"
                            v-model="formData.professionalIdNumber"
                            class="form-input"
                            placeholder="Enter ID number"
                        />
                      </div>
                      <div class="form-group">
                        <label class="form-label">Certification Expiry Date</label>
                        <input
                            type="date"
                            v-model="formData.certificationExpiry"
                            class="form-input"
                        />
                      </div>
                    </div>

                    <!-- Existing Professional Lookup -->
                    <div class="lookup-section">
                      <div class="lookup-header">
                        <span class="lookup-divider">or</span>
                      </div>
                      <div class="lookup-form">
                        <p class="lookup-text">Already registered? Search by certificate number:</p>
                        <div class="lookup-input-group">
                          <input
                              type="text"
                              v-model="professionalSearch"
                              class="form-input"
                              placeholder="Enter certificate number"
                          />
                          <button
                              class="btn btn-secondary"
                              @click="searchProfessional"
                              :disabled="!professionalSearch || searchingProfessional"
                          >
                            <span v-if="searchingProfessional" class="spinner small"></span>
                            <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <circle cx="11" cy="11" r="8"></circle>
                              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                            </svg>
                            Search
                          </button>
                        </div>
                        <p v-if="professionalSearchError" class="text-danger small mt-2">{{ professionalSearchError }}</p>
                      </div>
                    </div>

                    <div class="form-actions">
                      <button
                          class="btn btn-primary btn-lg"
                          @click="nextStep"
                          :disabled="!canProceedStep3"
                      >
                        Continue
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                          <polyline points="12 5 19 12 12 19"></polyline>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Step 4: Document Upload -->
                <div v-else-if="currentStep === 4" key="step4" class="form-card">
                  <div class="form-card-header">
                    <div class="step-badge">
                      <span class="step-number">4</span>
                      <span class="step-text">Upload Documents</span>
                    </div>
                    <button class="back-btn" @click="prevStep">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="19" y1="12" x2="5" y2="12"></line>
                        <polyline points="12 19 5 12 12 5"></polyline>
                      </svg>
                      Back
                    </button>
                  </div>

                  <div class="form-card-body">
                    <p class="form-intro">
                      Upload the required documents for your building permit application. All documents should be in PDF format (max 10MB each).
                    </p>

                    <div class="documents-list">
                      <!-- Document upload items -->
                      <div v-for="doc in documentTypes" :key="doc.key" class="document-item">
                        <div class="doc-info">
                          <div class="doc-icon" :class="doc.iconClass">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                              <polyline points="14 2 14 8 20 8"></polyline>
                            </svg>
                          </div>
                          <div class="doc-details">
                            <strong>{{ doc.label }} <span v-if="doc.required" class="text-danger">*</span></strong>
                            <span>{{ doc.description }}</span>
                          </div>
                        </div>
                        <div class="doc-action">
                          <input
                              type="file"
                              :id="doc.key"
                              :accept="doc.accept"
                              @change="handleFileUpload($event, doc.key)"
                              class="hidden-input"
                          />
                          <label v-if="!uploadedFiles[doc.key]" :for="doc.key" class="upload-btn">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                              <polyline points="17 8 12 3 7 8"></polyline>
                              <line x1="12" y1="3" x2="12" y2="15"></line>
                            </svg>
                            Upload
                          </label>
                          <div v-else class="uploaded-file">
                            <span class="file-name">{{ uploadedFiles[doc.key].name }}</span>
                            <button @click="removeFile(doc.key)" class="remove-btn">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="form-actions">
                      <button
                          class="btn btn-primary btn-lg"
                          @click="nextStep"
                          :disabled="!canProceedStep4"
                      >
                        Review Application
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                          <polyline points="12 5 19 12 12 19"></polyline>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Step 5: Review & Submit -->
                <div v-else-if="currentStep === 5" key="step5" class="form-card">
                  <div class="form-card-header">
                    <div class="step-badge">
                      <span class="step-number">5</span>
                      <span class="step-text">Review & Submit</span>
                    </div>
                    <button class="back-btn" @click="prevStep">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="19" y1="12" x2="5" y2="12"></line>
                        <polyline points="12 19 5 12 12 5"></polyline>
                      </svg>
                      Back
                    </button>
                  </div>

                  <div class="form-card-body">
                    <p class="form-intro">
                      Please review your application details before submitting.
                    </p>

                    <!-- Summary Sections -->
                    <div class="summary-section">
                      <div class="summary-header">
                        <h4>Project Information</h4>
                        <button class="edit-btn" @click="goToStep(1)">Edit</button>
                      </div>
                      <div class="summary-grid">
                        <div class="summary-item">
                          <span class="summary-label">Development Type</span>
                          <span class="summary-value">{{ getDevelopmentTypeLabel(formData.developmentType) }}</span>
                        </div>
                        <div class="summary-item">
                          <span class="summary-label">Project Value</span>
                          <span class="summary-value">KES {{ formatAmount(formData.projectValue) }}</span>
                        </div>
                        <div class="summary-item">
                          <span class="summary-label">Project Title</span>
                          <span class="summary-value">{{ formData.projectTitle }}</span>
                        </div>
                        <div class="summary-item">
                          <span class="summary-label">Plot Number</span>
                          <span class="summary-value">{{ formData.plotNumber }}</span>
                        </div>
                      </div>
                    </div>

                    <div class="summary-section">
                      <div class="summary-header">
                        <h4>Plot & Owner Details</h4>
                        <button class="edit-btn" @click="goToStep(2)">Edit</button>
                      </div>
                      <div class="summary-grid">
                        <div class="summary-item">
                          <span class="summary-label">Plot Size</span>
                          <span class="summary-value">{{ formData.plotSize }} m²</span>
                        </div>
                        <div class="summary-item">
                          <span class="summary-label">Location</span>
                          <span class="summary-value">{{ selectedWard?.wardName }}, {{ selectedSubCounty?.subCountyName }}</span>
                        </div>
                        <div class="summary-item">
                          <span class="summary-label">Owner Name</span>
                          <span class="summary-value">{{ formData.ownerFullNames }}</span>
                        </div>
                        <div class="summary-item">
                          <span class="summary-label">Phone</span>
                          <span class="summary-value">{{ formData.ownerPhoneNumber }}</span>
                        </div>
                      </div>
                    </div>

                    <div class="summary-section">
                      <div class="summary-header">
                        <h4>Professional Details</h4>
                        <button class="edit-btn" @click="goToStep(3)">Edit</button>
                      </div>
                      <div class="summary-grid">
                        <div class="summary-item">
                          <span class="summary-label">Type</span>
                          <span class="summary-value">{{ getProfessionalTypeLabel(formData.professionalType) }}</span>
                        </div>
                        <div class="summary-item">
                          <span class="summary-label">Name</span>
                          <span class="summary-value">{{ formData.professionalName }}</span>
                        </div>
                        <div class="summary-item">
                          <span class="summary-label">Certificate No.</span>
                          <span class="summary-value">{{ formData.certificateNumber }}</span>
                        </div>
                        <div class="summary-item">
                          <span class="summary-label">Phone</span>
                          <span class="summary-value">{{ formData.professionalPhone }}</span>
                        </div>
                      </div>
                    </div>

                    <div class="summary-section">
                      <div class="summary-header">
                        <h4>Uploaded Documents</h4>
                        <button class="edit-btn" @click="goToStep(4)">Edit</button>
                      </div>
                      <div class="documents-summary">
                        <div v-for="item in uploadedFilesList" :key="item.key" class="doc-summary-item">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                          <span>{{ getDocumentLabel(item.key) }}</span>
                        </div>
                      </div>
                    </div>

                    <!-- What Happens Next Info -->
                    <div class="next-info-card">
                      <div class="next-info-header">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="16" x2="12" y2="12"></line>
                          <line x1="12" y1="8" x2="12.01" y2="8"></line>
                        </svg>
                        <span>What happens after you submit?</span>
                      </div>
                      <div class="next-info-content">
                        <ul>
                          <li>Your application will be reviewed by the Physical Planning department</li>
                          <li>Once reviewed, you will receive an <strong>invoice via SMS and Email</strong></li>
                          <li>Make payment using M-Pesa or other available methods</li>
                          <li>After payment confirmation, your permit will be processed</li>
                        </ul>
                      </div>
                    </div>

                    <!-- Declaration -->
                    <div class="declaration-box">
                      <label class="declaration-label">
                        <input type="checkbox" v-model="declarationAccepted" />
                        <span class="checkbox-mark"></span>
                        <span class="declaration-text">
                          I declare that the information provided in this application is true and correct to the best of my knowledge.
                        </span>
                      </label>
                    </div>

                    <div class="form-actions">
                      <button
                          class="btn btn-success btn-lg"
                          @click="submitApplication"
                          :disabled="!declarationAccepted || submitting"
                      >
                        <span v-if="submitting" class="spinner white"></span>
                        <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                        {{ submitting ? 'Submitting...' : 'Submit Application' }}
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Success State -->
                <div v-else-if="currentStep === 6" key="step6" class="form-card success-card">
                  <div class="form-card-body">
                    <div class="success-content">
                      <div class="success-icon">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                          <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                      </div>
                      <h2>Application Submitted!</h2>
                      <p class="success-message">Your building permit application has been submitted successfully and is now pending review.</p>

                      <div class="reference-card">
                        <span class="ref-label">Application Reference</span>
                        <span class="ref-number">{{ applicationReference }}</span>
                        <p class="ref-note">Please save this reference number for tracking your application</p>
                      </div>

                      <div class="notification-card">
                        <div class="notification-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                          </svg>
                        </div>
                        <div class="notification-text">
                          <strong>Invoice Notification</strong>
                          <p>Once your application is reviewed, you will receive an invoice via SMS to <strong>{{ formData.ownerPhoneNumber }}</strong></p>
                        </div>
                      </div>

                      <div class="success-actions">
                        <button class="btn btn-primary" @click="resetForm">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                          </svg>
                          New Application
                        </button>
                        <router-link to="/" class="btn btn-secondary">
                          Back to Home
                        </router-link>
                      </div>
                    </div>
                  </div>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </section>
    </main>

    <FooterComponent />
  </div>
</template>

<script>
import { biller, econstruction, execute } from "@/api";
import HeaderComponent from "@/components/HeaderComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";

// Icon components
const ResidentialIcon = {
  template: `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>`
};
const CommercialIcon = {
  template: `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="4" y="2" width="16" height="20" rx="2"></rect><line x1="9" y1="6" x2="9" y2="6.01"></line><line x1="15" y1="6" x2="15" y2="6.01"></line><line x1="9" y1="10" x2="9" y2="10.01"></line><line x1="15" y1="10" x2="15" y2="10.01"></line></svg>`
};
const IndustrialIcon = {
  template: `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M2 20h20M5 20V10l7-7 7 7v10"></path></svg>`
};
const InstitutionalIcon = {
  template: `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 2L2 7l10 5 10-5-10-5z"></path><path d="M2 17l10 5 10-5M2 12l10 5 10-5"></path></svg>`
};

export default {
  name: "BuildingPermits",
  components: { HeaderComponent, FooterComponent, ResidentialIcon, CommercialIcon, IndustrialIcon, InstitutionalIcon },
  data() {
    return {
      county: null,
      currentStep: 1,
      steps: ['Project', 'Plot & Owner', 'Professional', 'Documents', 'Review'],
      error: '',
      submitting: false,

      formData: {
        developmentType: '',
        projectTitle: '',
        projectValue: '',
        projectDescription: '',
        plotNumber: '',
        upnNumber: '',
        plotSize: '',
        landType: '',
        landUse: '',
        ownerFullNames: '',
        ownerIdNumber: '',
        ownerPhoneNumber: '',
        ownerEmail: '',
        ownerKraPin: '',
        ownerAddress: '',
        professionalType: '',
        professionalName: '',
        certificateNumber: '',
        professionalPhone: '',
        professionalEmail: '',
        professionalIdNumber: '',
        certificationExpiry: ''
      },

      developmentTypes: [
        { value: 'residential', label: 'Residential', icon: 'ResidentialIcon', color: 'blue' },
        { value: 'commercial', label: 'Commercial', icon: 'CommercialIcon', color: 'green' },
        { value: 'industrial', label: 'Industrial', icon: 'IndustrialIcon', color: 'orange' },
        { value: 'institutional', label: 'Institutional', icon: 'InstitutionalIcon', color: 'purple' }
      ],

      documentTypes: [
        { key: 'landOwnership', label: 'Title Deed / Land Ownership', description: 'Proof of land ownership or lease', required: true, accept: '.pdf', iconClass: '' },
        { key: 'identificationCard', label: "Owner's ID Card", description: 'National ID or Passport copy', required: true, accept: '.pdf,.jpg,.jpeg,.png', iconClass: '' },
        { key: 'kraPin', label: 'KRA PIN Certificate', description: 'Tax registration certificate', required: false, accept: '.pdf', iconClass: '' },
        { key: 'architecturalDrawings', label: 'Architectural Drawings', description: 'Floor plans, elevations, sections', required: true, accept: '.pdf', iconClass: 'blue' },
        { key: 'structuralDrawings', label: 'Structural Drawings', description: 'Foundation, beams, columns details', required: true, accept: '.pdf', iconClass: 'blue' },
        { key: 'sitePlan', label: 'Site Plan / Survey Plan', description: 'Showing plot boundaries', required: false, accept: '.pdf', iconClass: 'green' }
      ],

      subCounties: [],
      wards: [],
      selectedSubCounty: null,
      selectedWard: null,
      professionalSearch: '',
      searchingProfessional: false,
      professionalSearchError: '',
      professionalId: null,
      searchingPlot: false,
      plotSearched: false,
      plotDetails: null,
      uploadedFiles: {},
      declarationAccepted: false,
      applicationReference: ''
    };
  },

  computed: {
    canProceedStep1() {
      return this.formData.developmentType && this.formData.projectTitle.trim() && this.formData.projectValue && this.formData.plotNumber.trim() && this.formData.projectDescription.trim();
    },
    canProceedStep2() {
      return this.formData.plotSize && this.formData.landType && this.formData.landUse && this.selectedSubCounty && this.selectedWard && this.formData.ownerFullNames.trim() && this.formData.ownerIdNumber.trim() && this.formData.ownerPhoneNumber.trim();
    },
    canProceedStep3() {
      return this.formData.professionalType && this.formData.professionalName.trim() && this.formData.certificateNumber.trim() && this.formData.professionalPhone.trim() && this.formData.professionalEmail.trim() && this.formData.professionalIdNumber.trim();
    },
    canProceedStep4() {
      return this.uploadedFiles.landOwnership && this.uploadedFiles.identificationCard && this.uploadedFiles.architecturalDrawings && this.uploadedFiles.structuralDrawings;
    },
    /* eslint-disable */
    uploadedFilesList() {
      return Object.entries(this.uploadedFiles)
          .filter(([key, file]) => file)
          .map(([key, file]) => ({ key, file }));
    }
  },

  watch: {
    selectedSubCounty(newVal) {
      if (newVal) this.getWards(newVal.subCountyID);
    }
  },

  mounted() {
    this.getCounty();
    this.getSubCounties();
  },

  methods: {
    async getCounty() {
      try {
        const data = new FormData();
        data.append("function", "getCounty");
        const res = await execute(data, biller);
        if (res.data.success) this.county = res.data.data.county;
      } catch (e) { console.error(e); }
    },

    async getSubCounties() {
      try {
        const data = new FormData();
        data.append("function", "getSubCounty");
        const res = await execute(data, biller);
        if (res.data.success) this.subCounties = res.data.data.subCounties;
      } catch (e) { console.error(e); }
    },

    async getWards(subCountyID) {
      try {
        const data = new FormData();
        data.append("function", "getWards");
        data.append("subCountyID", subCountyID);
        const res = await execute(data, biller);
        if (res.data.success) this.wards = res.data.data.wards;
      } catch (e) { console.error(e); }
    },

    onSubCountyChange() {
      this.selectedWard = null;
      this.wards = [];
    },

    clearPlotDetails() {
      this.plotDetails = null;
      this.plotSearched = false;
    },

    async searchPlot() {
      if (!this.formData.plotNumber.trim()) return;
      this.searchingPlot = true;
      this.plotSearched = false;
      this.plotDetails = null;
      try {
        const data = new FormData();
        data.append("function", "searchPlot");
        data.append("plotNumber", this.formData.plotNumber.trim());
        const res = await execute(data, econstruction);
        if (res.data.success && res.data.data.plot) {
          const plot = res.data.data.plot;
          this.plotDetails = plot;

          // Auto-fill form fields from plot details
          this.formData.upnNumber = plot.UPN || '';
          this.formData.plotSize = plot.percelSize || '';
          this.formData.landUse = plot.useName || '';
          this.formData.ownerFullNames = plot.ownerNames || '';
          this.formData.ownerPhoneNumber = plot.ownerPhoneNo || '';
          this.formData.ownerEmail = plot.ownerEmail || '';
          this.formData.ownerAddress = plot.percelAddress || '';

          // Try to auto-select sub-county and ward
          if (plot.subCountyID && this.subCounties.length > 0) {
            const matchedSubCounty = this.subCounties.find(sc => sc.subCountyID === plot.subCountyID);
            if (matchedSubCounty) {
              this.selectedSubCounty = matchedSubCounty;
              await this.getWards(matchedSubCounty.subCountyID);

              // Auto-select ward after wards are loaded
              if (plot.wardID && this.wards.length > 0) {
                const matchedWard = this.wards.find(w => w.wardID === plot.wardID);
                if (matchedWard) {
                  this.selectedWard = matchedWard;
                }
              }
            }
          }

          this.plotSearched = true;
        } else {
          this.error = res.data.message || 'Plot not found';
          setTimeout(() => { this.error = ''; }, 3000);
        }
      } catch (e) {
        console.error('Plot search error:', e);
        this.error = 'Error searching for plot. Please try again.';
      } finally {
        this.searchingPlot = false;
      }
    },

    async searchProfessional() {
      if (!this.professionalSearch.trim()) return;
      this.searchingProfessional = true;
      this.professionalSearchError = '';
      try {
        const data = new FormData();
        data.append("function", "searchProfessional");
        data.append("certificateNumber", this.professionalSearch.trim());
        if (this.formData.professionalType) {
          data.append("professionalType", this.formData.professionalType);
        }
        const res = await execute(data, econstruction);
        if (res.data.success && res.data.data.professional) {
          const prof = res.data.data.professional;
          this.formData.professionalType = prof.professional_type;
          this.formData.professionalName = prof.full_names;
          this.formData.certificateNumber = prof.certificate_number;
          this.formData.professionalPhone = prof.phone_number;
          this.formData.professionalEmail = prof.email_address;
          this.formData.professionalIdNumber = prof.id_number;
          this.formData.certificationExpiry = prof.certification_expiry;
          this.professionalId = prof.id;
        } else {
          this.professionalSearchError = res.data.message || 'Professional not found. Please enter details manually.';
        }
      } catch (e) {
        this.professionalSearchError = 'Error searching. Please try again.';
      } finally {
        this.searchingProfessional = false;
      }
    },

    nextStep() { if (this.currentStep < 5) this.currentStep++; },
    prevStep() { if (this.currentStep > 1) this.currentStep--; },
    goToStep(step) { this.currentStep = step; },

    handleFileUpload(event, docType) {
      const file = event.target.files[0];
      if (file) {
        if (file.size > 10 * 1024 * 1024) { this.error = 'File size exceeds 10MB limit'; return; }
        this.uploadedFiles[docType] = file;
        this.error = '';
      }
    },

    removeFile(docType) { this.uploadedFiles[docType] = null; },

    getDocumentLabel(key) {
      const doc = this.documentTypes.find(d => d.key === key);
      return doc ? doc.label : key;
    },

    getDevelopmentTypeLabel(value) {
      const type = this.developmentTypes.find(t => t.value === value);
      return type ? type.label : value;
    },

    getProfessionalTypeLabel(type) {
      const types = { architect: 'Architect', civilEngineer: 'Civil Engineer', structuralEngineer: 'Structural Engineer', electricalEngineer: 'Electrical Engineer', mechanicalEngineer: 'Mechanical Engineer', landSurveyor: 'Land Surveyor', urbanPlanner: 'Urban Planner' };
      return types[type] || type;
    },

    formatAmount(amount) {
      if (!amount) return '0';
      return new Intl.NumberFormat('en-KE').format(parseFloat(amount));
    },

    async submitApplication() {
      this.submitting = true;
      this.error = '';
      try {
        let professionalId = this.professionalId;

        // If no existing professional, add new one
        if (!professionalId) {
          const profData = new FormData();
          profData.append("function", "addProfessional");
          profData.append("professionalType", this.formData.professionalType);
          profData.append("fullNames", this.formData.professionalName);
          profData.append("certificateNumber", this.formData.certificateNumber);
          profData.append("phoneNumber", this.formData.professionalPhone);
          profData.append("emailAddress", this.formData.professionalEmail);
          profData.append("idNumber", this.formData.professionalIdNumber);
          profData.append("certificationExpiry", this.formData.certificationExpiry || '2025-12-31');
          profData.append("sessionNames", sessionStorage.getItem("sessionNames") || this.formData.ownerFullNames);
          profData.append("sessionIdNo", sessionStorage.getItem("sessionIdNo") || this.formData.ownerIdNumber);

          const profRes = await execute(profData, econstruction);
          if (profRes.data.success) {
            professionalId = profRes.data.data.professional_id;
          } else {
            throw new Error(profRes.data.message || 'Failed to add professional');
          }
        }

        const applicationData = {
          developmentType: this.formData.developmentType,
          projectTitle: this.formData.projectTitle,
          projectValue: this.formData.projectValue,
          projectDescription: this.formData.projectDescription,
          professionalId: professionalId,
          plotNumber: this.formData.plotNumber,
          upnNumber: this.formData.upnNumber,
          plotDetails: {
            plotSize: this.formData.plotSize,
            landType: this.formData.landType,
            landUse: this.formData.landUse,
            ownerFullNames: this.formData.ownerFullNames,
            ownerIdNumber: this.formData.ownerIdNumber,
            ownerPhoneNumber: this.formData.ownerPhoneNumber,
            ownerEmail: this.formData.ownerEmail,
            ownerKraPin: this.formData.ownerKraPin,
            ownerAddress: this.formData.ownerAddress,
            subCounty: this.selectedSubCounty?.subCountyName,
            ward: this.selectedWard?.wardName
          },
          sessionNames: sessionStorage.getItem("sessionNames") || this.formData.ownerFullNames,
          sessionIdNo: sessionStorage.getItem("sessionIdNo") || this.formData.ownerIdNumber,
          documents: Object.keys(this.uploadedFiles).filter(k => this.uploadedFiles[k]).map(k => ({ fileId: k, documentType: k }))
        };

        const data = new FormData();
        data.append("function", "submitApplication");
        data.append("applicationData", JSON.stringify(applicationData));
        Object.keys(this.uploadedFiles).forEach(key => { if (this.uploadedFiles[key]) data.append(key, this.uploadedFiles[key]); });

        const res = await execute(data, econstruction);
        if (res.data.success) {
          this.applicationReference = res.data.data.applicationNumber;
          this.currentStep = 6;
        } else {
          this.error = res.data.message || 'Failed to submit application.';
        }
      } catch (e) {
        this.error = e.message || 'An error occurred while submitting.';
      } finally {
        this.submitting = false;
      }
    },

    resetForm() {
      this.currentStep = 1;
      this.formData = { developmentType: '', projectTitle: '', projectValue: '', projectDescription: '', plotNumber: '', upnNumber: '', plotSize: '', landType: '', landUse: '', ownerFullNames: '', ownerIdNumber: '', ownerPhoneNumber: '', ownerEmail: '', ownerKraPin: '', ownerAddress: '', professionalType: '', professionalName: '', certificateNumber: '', professionalPhone: '', professionalEmail: '', professionalIdNumber: '', certificationExpiry: '' };
      this.selectedSubCounty = null;
      this.selectedWard = null;
      this.professionalId = null;
      this.plotSearched = false;
      this.plotDetails = null;
      this.uploadedFiles = {};
      this.declarationAccepted = false;
      this.applicationReference = '';
      this.error = '';
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.page-wrapper { min-height: 100vh; display: flex; flex-direction: column; background: #f8fafc; }
.main-content { flex: 1; padding-top: 72px; }
.page-hero { background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%); padding: 48px 0 60px; color: white; }
.container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }
.breadcrumb-nav { display: flex; align-items: center; gap: 8px; margin-bottom: 16px; font-size: 14px; }
.breadcrumb-link { color: rgba(255,255,255,0.7); text-decoration: none; }
.breadcrumb-link:hover { color: white; }
.breadcrumb-separator { color: rgba(255,255,255,0.4); }
.breadcrumb-current { color: white; }
.page-title { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 32px; font-weight: 800; margin-bottom: 8px; }
.page-subtitle { opacity: 0.9; font-size: 16px; margin-bottom: 32px; }

.progress-stepper { display: flex; justify-content: center; background: rgba(255,255,255,0.1); backdrop-filter: blur(10px); border-radius: 16px; padding: 24px 32px; }
.step-item { display: flex; align-items: center; gap: 12px; }
.step-connector { width: 40px; height: 3px; background: rgba(255,255,255,0.3); margin: 0 8px; border-radius: 2px; }
.step-item.completed .step-connector { background: #10b981; }
.step-circle { width: 36px; height: 36px; border-radius: 50%; background: rgba(255,255,255,0.2); display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 14px; }
.step-item.active .step-circle { background: white; color: #0f4c81; transform: scale(1.1); box-shadow: 0 4px 12px rgba(0,0,0,0.2); }
.step-item.completed .step-circle { background: #10b981; color: white; }
.step-label { font-size: 13px; font-weight: 500; opacity: 0.8; }
.step-item.active .step-label, .step-item.completed .step-label { opacity: 1; font-weight: 600; }

.content-section { padding: 48px 0 80px; }
.content-grid { display: grid; grid-template-columns: 1fr 1.4fr; gap: 32px; align-items: start; }
.info-panel { position: sticky; top: 96px; }
.info-card { background: white; border-radius: 16px; padding: 32px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); }
.info-card-header { display: flex; align-items: center; gap: 16px; margin-bottom: 20px; }
.info-icon { width: 56px; height: 56px; background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%); border-radius: 14px; display: flex; align-items: center; justify-content: center; color: #0f4c81; }
.info-card-header h3 { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 22px; font-weight: 700; color: #1e293b; }
.info-description { color: #64748b; line-height: 1.6; margin-bottom: 24px; }
.info-features { display: flex; flex-direction: column; gap: 16px; margin-bottom: 24px; }
.feature-item { display: flex; gap: 12px; padding: 16px; background: #f8fafc; border-radius: 12px; }
.feature-icon { width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.feature-icon.green { background: #d1fae5; color: #059669; }
.feature-icon.blue { background: #dbeafe; color: #2563eb; }
.feature-icon.purple { background: #dbeafe; color: #0f4c81; }
.feature-item strong { display: block; font-size: 14px; color: #1e293b; margin-bottom: 2px; }
.feature-item p { font-size: 13px; color: #64748b; margin: 0; }

.process-info { padding: 20px; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 12px; margin-bottom: 20px; }
.process-info h4 { display: flex; align-items: center; gap: 8px; font-size: 14px; font-weight: 600; color: #1e40af; margin-bottom: 12px; }
.process-info ol { padding-left: 20px; margin: 0; }
.process-info li { font-size: 13px; color: #3b82f6; margin-bottom: 6px; }

.required-docs { padding: 20px; background: #fef3c7; border: 1px solid #fcd34d; border-radius: 12px; margin-bottom: 20px; }
.required-docs h4 { display: flex; align-items: center; gap: 8px; font-size: 14px; font-weight: 600; color: #92400e; margin-bottom: 12px; }
.required-docs ul { list-style: none; padding: 0; margin: 0 0 12px; }
.required-docs li { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #78350f; margin-bottom: 6px; }
.required-docs li svg { color: #059669; }
.docs-note { font-size: 12px; color: #a16207; margin: 0; }

.help-contact { padding: 20px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; }
.help-contact h4 { display: flex; align-items: center; gap: 8px; font-size: 14px; font-weight: 600; color: #475569; margin-bottom: 12px; }
.contact-info { display: flex; flex-direction: column; gap: 8px; }
.contact-item { display: flex; align-items: center; gap: 10px; font-size: 14px; color: #64748b; }

.form-card { background: white; border-radius: 16px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); overflow: hidden; }
.form-card-header { padding: 24px 32px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; display: flex; align-items: center; justify-content: space-between; }
.step-badge { display: flex; align-items: center; gap: 10px; }
.step-badge .step-number { width: 28px; height: 28px; background: #0f4c81; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 600; font-size: 13px; }
.step-badge .step-text { font-weight: 600; color: #1e293b; }
.back-btn { display: flex; align-items: center; gap: 8px; padding: 8px 16px; background: none; border: 1px solid #e2e8f0; border-radius: 8px; color: #64748b; font-size: 14px; cursor: pointer; }
.back-btn:hover { background: #f1f5f9; color: #1e293b; }
.form-card-body { padding: 32px; }
.form-intro { color: #64748b; margin-bottom: 24px; line-height: 1.6; }

.alert { display: flex; align-items: center; gap: 12px; padding: 14px 16px; border-radius: 10px; font-size: 14px; }
.alert-error { background: #fef2f2; border: 1px solid #fecaca; color: #dc2626; }
.alert-close { margin-left: auto; background: none; border: none; cursor: pointer; padding: 4px; color: inherit; }
.mb-4 { margin-bottom: 16px; }

.development-type-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 20px; }
.type-card { padding: 20px 12px; background: #f8fafc; border: 2px solid #e2e8f0; border-radius: 12px; text-align: center; cursor: pointer; transition: all 0.2s; }
.type-card:hover { border-color: #0f4c81; }
.type-card.selected { background: #dbeafe; border-color: #0f4c81; }
.hidden-radio { display: none; }
.type-icon { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; margin: 0 auto 10px; }
.type-icon.blue { background: #dbeafe; color: #2563eb; }
.type-icon.green { background: #d1fae5; color: #059669; }
.type-icon.orange { background: #ffedd5; color: #ea580c; }
.type-icon.purple { background: #ede9fe; color: #7c3aed; }
.type-card strong { display: block; font-size: 13px; color: #1e293b; }

.section-header { margin-bottom: 20px; }
.section-header h4 { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 16px; font-weight: 600; color: #1e293b; padding-bottom: 10px; border-bottom: 2px solid #e2e8f0; }
.form-group { margin-bottom: 20px; }
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.form-label { display: flex; align-items: center; gap: 8px; font-size: 14px; font-weight: 600; color: #1e293b; margin-bottom: 10px; }
.text-danger { color: #dc2626; }
.form-input, .form-textarea { width: 100%; padding: 14px 16px; border: 2px solid #e2e8f0; border-radius: 10px; font-family: 'DM Sans', sans-serif; font-size: 15px; transition: all 0.2s; background: white; }
.form-input:focus, .form-textarea:focus { outline: none; border-color: #0f4c81; box-shadow: 0 0 0 4px rgba(15, 76, 129, 0.1); }
.select-wrapper { position: relative; }
.form-select { width: 100%; padding: 14px 44px 14px 16px; border: 2px solid #e2e8f0; border-radius: 10px; font-family: 'DM Sans', sans-serif; font-size: 15px; background: white; cursor: pointer; appearance: none; }
.form-select:focus { outline: none; border-color: #0f4c81; box-shadow: 0 0 0 4px rgba(15, 76, 129, 0.1); }
.form-select:disabled { background: #f8fafc; cursor: not-allowed; }
.select-icon { position: absolute; right: 14px; top: 50%; transform: translateY(-50%); color: #94a3b8; pointer-events: none; }

.input-with-button { display: flex; gap: 8px; }
.input-with-button .form-input { flex: 1; }
.fetch-btn { padding: 0 16px; background: #0f4c81; color: white; border: none; border-radius: 10px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background 0.2s; }
.fetch-btn:hover:not(:disabled) { background: #0a3459; }
.fetch-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.form-hint { display: block; font-size: 12px; color: #94a3b8; margin-top: 6px; }
.form-hint.success { color: #16a34a; display: flex; align-items: center; gap: 4px; }

/* Plot Details Card */
.plot-details-card { background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%); border: 1px solid #a7f3d0; border-radius: 12px; padding: 20px; margin-top: 20px; }
.plot-details-header { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; }
.plot-icon { width: 44px; height: 44px; background: #10b981; border-radius: 10px; display: flex; align-items: center; justify-content: center; color: white; }
.plot-details-header h4 { font-size: 16px; font-weight: 700; color: #065f46; margin: 0; }
.plot-details-header p { font-size: 14px; color: #047857; margin: 0; font-weight: 600; }
.clear-plot-btn { margin-left: auto; padding: 8px; background: rgba(0,0,0,0.1); border: none; border-radius: 8px; color: #065f46; cursor: pointer; transition: all 0.2s; }
.clear-plot-btn:hover { background: rgba(0,0,0,0.15); }
.plot-details-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin-bottom: 16px; }
.plot-detail-item { background: white; padding: 12px; border-radius: 8px; }
.detail-label { display: block; font-size: 11px; color: #6b7280; text-transform: uppercase; margin-bottom: 4px; }
.detail-value { font-size: 14px; font-weight: 600; color: #1e293b; }
.detail-value.text-warning { color: #d97706; }
.status-badge { display: inline-block; padding: 4px 10px; border-radius: 20px; font-size: 12px; font-weight: 600; }
.status-badge.active { background: #dcfce7; color: #16a34a; }
.status-badge.inactive { background: #fef2f2; color: #dc2626; }
.plot-note { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #047857; margin: 0; padding-top: 12px; border-top: 1px solid #a7f3d0; }

.lookup-section { margin-top: 24px; padding-top: 24px; border-top: 1px solid #e2e8f0; }
.lookup-header { text-align: center; margin-bottom: 16px; }
.lookup-divider { background: white; padding: 0 16px; color: #94a3b8; font-size: 14px; }
.lookup-text { font-size: 14px; color: #64748b; margin-bottom: 12px; }
.lookup-input-group { display: flex; gap: 12px; }
.lookup-input-group .form-input { flex: 1; }

.documents-list { display: flex; flex-direction: column; gap: 12px; }
.document-item { display: flex; align-items: center; justify-content: space-between; padding: 16px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; }
.doc-info { display: flex; align-items: center; gap: 12px; }
.doc-icon { width: 44px; height: 44px; background: #f1f5f9; border-radius: 10px; display: flex; align-items: center; justify-content: center; color: #64748b; }
.doc-icon.blue { background: #dbeafe; color: #2563eb; }
.doc-icon.green { background: #d1fae5; color: #059669; }
.doc-details strong { display: block; font-size: 14px; color: #1e293b; margin-bottom: 2px; }
.doc-details span { font-size: 12px; color: #64748b; }
.hidden-input { display: none; }
.upload-btn { display: flex; align-items: center; gap: 6px; padding: 10px 16px; background: #0f4c81; color: white; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; }
.upload-btn:hover { background: #0a3459; }
.uploaded-file { display: flex; align-items: center; gap: 8px; padding: 8px 12px; background: #d1fae5; border-radius: 8px; }
.file-name { font-size: 13px; color: #047857; max-width: 150px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.remove-btn { padding: 4px; background: none; border: none; color: #dc2626; cursor: pointer; }

.summary-section { margin-bottom: 24px; padding-bottom: 24px; border-bottom: 1px solid #e2e8f0; }
.summary-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.summary-header h4 { font-size: 16px; font-weight: 600; color: #1e293b; }
.edit-btn { padding: 6px 14px; background: #f1f5f9; border: 1px solid #e2e8f0; border-radius: 6px; font-size: 13px; color: #0f4c81; cursor: pointer; }
.edit-btn:hover { background: #e2e8f0; }
.summary-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.summary-item { padding: 12px; background: #f8fafc; border-radius: 8px; }
.summary-label { display: block; font-size: 11px; color: #64748b; text-transform: uppercase; margin-bottom: 4px; }
.summary-value { font-size: 14px; font-weight: 500; color: #1e293b; }
.documents-summary { display: flex; flex-wrap: wrap; gap: 8px; }
.doc-summary-item { display: flex; align-items: center; gap: 6px; padding: 8px 14px; background: #d1fae5; border-radius: 20px; font-size: 13px; color: #047857; }

.next-info-card { background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 12px; padding: 20px; margin-bottom: 24px; }
.next-info-header { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; color: #1e40af; font-weight: 600; }
.next-info-content ul { list-style: none; padding: 0; margin: 0; }
.next-info-content li { padding: 8px 0 8px 24px; font-size: 14px; color: #3b82f6; position: relative; }
.next-info-content li::before { content: '→'; position: absolute; left: 0; color: #60a5fa; }

.declaration-box { padding: 20px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; margin-bottom: 24px; }
.declaration-label { display: flex; align-items: flex-start; gap: 12px; cursor: pointer; }
.declaration-label input[type="checkbox"] { display: none; }
.checkbox-mark { width: 22px; height: 22px; border: 2px solid #d1d5db; border-radius: 6px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; }
.declaration-label input:checked + .checkbox-mark { background: #0f4c81; border-color: #0f4c81; }
.declaration-label input:checked + .checkbox-mark::after { content: '✓'; color: white; font-weight: bold; }
.declaration-text { font-size: 14px; color: #475569; line-height: 1.6; }

.btn { display: inline-flex; align-items: center; justify-content: center; gap: 10px; padding: 14px 24px; border-radius: 10px; font-family: 'DM Sans', sans-serif; font-size: 15px; font-weight: 600; cursor: pointer; border: none; text-decoration: none; }
.btn-lg { padding: 16px 32px; font-size: 16px; }
.btn-primary { background: #0f4c81; color: white; }
.btn-primary:hover:not(:disabled) { background: #0a3459; }
.btn-success { background: #059669; color: white; }
.btn-success:hover:not(:disabled) { background: #047857; }
.btn-secondary { background: #f1f5f9; color: #475569; border: 1px solid #e2e8f0; }
.btn-secondary:hover { background: #e2e8f0; }
.btn:disabled { opacity: 0.6; cursor: not-allowed; }
.form-actions { display: flex; justify-content: flex-end; margin-top: 24px; padding-top: 24px; border-top: 1px solid #e2e8f0; }

.spinner { width: 20px; height: 20px; border: 2px solid rgba(0,0,0,0.1); border-top-color: currentColor; border-radius: 50%; animation: spin 0.8s linear infinite; }
.spinner.white { border-color: rgba(255,255,255,0.3); border-top-color: white; }
.spinner.small { width: 16px; height: 16px; }
@keyframes spin { to { transform: rotate(360deg); } }

.success-card .form-card-body { padding: 48px; }
.success-content { text-align: center; }
.success-icon { width: 80px; height: 80px; background: #d1fae5; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #059669; margin: 0 auto 24px; }
.success-content h2 { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 24px; font-weight: 700; color: #059669; margin-bottom: 8px; }
.success-message { color: #64748b; margin-bottom: 32px; }
.reference-card { background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%); border-radius: 14px; padding: 24px; color: white; margin-bottom: 24px; }
.ref-label { font-size: 12px; opacity: 0.8; text-transform: uppercase; letter-spacing: 1px; }
.ref-number { display: block; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 24px; font-weight: 800; margin: 8px 0; letter-spacing: 1px; }
.ref-note { font-size: 13px; opacity: 0.8; margin: 0; }
.notification-card { display: flex; align-items: flex-start; gap: 16px; padding: 20px; background: #fef3c7; border: 1px solid #fcd34d; border-radius: 12px; margin-bottom: 24px; text-align: left; }
.notification-icon { width: 44px; height: 44px; background: #fbbf24; border-radius: 10px; display: flex; align-items: center; justify-content: center; color: white; flex-shrink: 0; }
.notification-text strong { display: block; color: #92400e; margin-bottom: 4px; }
.notification-text p { font-size: 14px; color: #a16207; margin: 0; }
.success-actions { display: flex; gap: 12px; justify-content: center; }

.slide-fade-enter-active { transition: all 0.3s ease; }
.slide-fade-leave-active { transition: all 0.2s ease; }
.slide-fade-enter-from { opacity: 0; transform: translateX(20px); }
.slide-fade-leave-to { opacity: 0; transform: translateX(-20px); }

.mt-4 { margin-top: 24px; }
.mt-2 { margin-top: 8px; }
.small { font-size: 13px; }

@media (max-width: 1024px) {
  .content-grid { grid-template-columns: 1fr; }
  .info-panel { position: static; }
  .progress-stepper { flex-wrap: wrap; gap: 16px; }
  .step-connector { display: none; }
  .development-type-grid { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 768px) {
  .form-card-body { padding: 24px; }
  .form-row { grid-template-columns: 1fr; }
  .summary-grid { grid-template-columns: 1fr; }
  .success-actions { flex-direction: column; }
  .page-title { font-size: 26px; }
  .lookup-input-group { flex-direction: column; }
  .plot-details-grid { grid-template-columns: 1fr; }
}
</style>